import { TableContainer, Thead, Th, Box, Text, Divider } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { AxiosResponse, HttpStatusCode } from 'axios';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAlertContext } from '../../../../../context/AlertProvider';
import { useApi } from '../../../../../context/ApiProvider';
import { CourseInterface } from '../../../../../shared/type/course.type';
import CustomAlert from '../../../../CustomAlert/CustomAlert';
import CustomChakraButton from '../../../../CustomChakraButton/CustomChakraButton';
import { DataTable } from '../../../../DataTable/DataTable';
import IconComponent from '../../../../IconComponent/IconComponent';
import AddEditMealCategory from './AddEditMealCategory/AddEditMealCategory';
import { StickyScrollableTableContainer } from '../../../../DataTable/StickyScrollableTableContainer';
import { DeletedElementInterface } from '../../../../../shared/type/deletedElement.type';

const defaultDeleteElement = {
  id: 0,
  isOpen: false
};

const MealCategories = () => {
  const { apiTenantCourseController } = useApi();
  const { id: tenantId } = useParams();
  const { setShow, setAlertProperties } = useAlertContext();
  const columnHelper = createColumnHelper<CourseInterface>();

  const [mealCategories, setMealCategories] = useState<CourseInterface[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteElement, setDeleteElement] = useState<DeletedElementInterface>(defaultDeleteElement);
  const [addEditCategoryPopUp, setAddEditCategoryPopUp] = useState<{
    isOpen: boolean;
    mode: 'add' | 'edit';
  }>({ isOpen: false, mode: 'add' });
  const [editedMealCategory, setEditedMealCategory] = useState<CourseInterface>();

  const fetchMealCategories = async () => {
    setIsLoading(true);
    try {
      const response = await apiTenantCourseController('').get(`/${tenantId}`);
      setMealCategories(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMealCategories();
  }, [tenantId]);

  const columns = [
    columnHelper.accessor('name', {
      header: 'Nazwa i kategoria posiłku',
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900 w-40 truncate">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('mealDistributionDto.name', {
      header: 'Przypisana wydawka',
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-600 w-40 truncate">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('stakes', {
      header: 'Przypisana stawka',
      cell: (info) => (
        <div className="text-sm text-grayLight-600 flex flex-col gap-2">
          {info.getValue().map((item, index) => (
            <div key={index} className="border-t border-grayLight-200 pt-2">
              {item.name}
            </div>
          ))}
        </div>
      )
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Edycja',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex gap-4">
            <button
              className="text-sm font-semibold text-grayLight-600"
              onClick={() => setDeleteElement({ isOpen: true, id: item.id })}>
              Usuń
            </button>
            <button
              className="text-sm font-semibold text-grayWarm-950"
              onClick={() => {
                setEditedMealCategory(item);
                setAddEditCategoryPopUp({ isOpen: true, mode: 'edit' });
              }}>
              Edytuj
            </button>
          </div>
        );
      }
    })
  ];

  return (
    <section className="MealCategories p-spacing-xl w-full">
      <Box mb={6} className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <Text
            whiteSpace="normal"
            overflow="hidden"
            textOverflow="ellipsis"
            className="font-semibold text-base normal-case text-grayLight-900">
            Kategorie posiłków
          </Text>
          <Text
            whiteSpace="normal"
            overflow="hidden"
            textOverflow="ellipsis"
            className="w-3/4 font-normal text-sm normal-case text-grayLight-700">
            Tutaj możesz tworzyć i edytować Kategorie Posiłków jakie będą sprzedawane w stworzonych
            Stawkach w trakcie wybranych Wydawek np. Stawka “Zestaw obiadowy” ma przypisane do niej
            Kategorie Posiłku “Zupa” i “Drugie Danie” i jest wydawana tylko w trakcie Wydawki
            “Obiad”.
          </Text>
        </div>
        <CustomChakraButton
          size="md"
          hierarchy="primary"
          iconColor="#292524"
          iconPosition="left"
          icon="plus"
          buttonProps={{
            onClick: () => setAddEditCategoryPopUp({ isOpen: true, mode: 'add' })
          }}>
          Dodaj kategorię
        </CustomChakraButton>
      </Box>

      <StickyScrollableTableContainer
        columns={columns}
        data={mealCategories}
        isLoading={isLoading}
        enableSearch={true}
      />

      <CustomAlert
        header="Usunąć kategorię?"
        content="Usunięcie kategorii powoduje usunięcie wszystkich przypisanych do niej jadłospisów."
        confirmButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'warning' }}
        confirmButtonText="Usuń"
        onConfirmAction={async () => {
          try {
            const response = await apiTenantCourseController('').delete(`/${deleteElement.id}`);
            if (response.status === HttpStatusCode.Ok) {
              setAlertProperties({
                timeout: 9000,
                title: 'Sukces',
                description: 'Kategoria została usunięta',
                status: 'success'
              });
              setShow(true);
              setDeleteElement(defaultDeleteElement);
              
            } else {
              throw new Error();
            }
          } catch (error) {
            setAlertProperties({
              timeout: 9000,
              title: 'Błąd',
              description: 'Nie udało się usunąć kategorii',
              status: 'error'
            });
            setShow(true);
          } finally {
            fetchMealCategories();
          }
        }}
        cancelButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'secondaryGray' }}
        cancelButtonText="Anuluj"
        onCancelAction={() => {
          setDeleteElement(defaultDeleteElement);
          fetchMealCategories();
        }}
        handleOpen={deleteElement.isOpen}
        icon={
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-error-100">
            <IconComponent iconName="trash" />
          </div>
        }
      />

      {addEditCategoryPopUp.isOpen && (
        <AddEditMealCategory
          initData={editedMealCategory}
          mode={addEditCategoryPopUp.mode}
          isOpen={addEditCategoryPopUp.isOpen}
          onClose={() => {
            setAddEditCategoryPopUp({ isOpen: false, mode: 'add' });
            fetchMealCategories();
          }}
        />
      )}
    </section>
  );
};

export default MealCategories;
